import { Card, Button, Row, Col, Container } from 'react-bootstrap';
import type { Plan } from '../types/types';
import mostPopularBadge from '../assets/MostPopularBadge.png';
import SupplierPlanImage from '../assets/SupplierPlanImage.jpg';
import ManagerPlanImage from '../assets/ManagerPlanImage.jpg';
import { useState } from 'react';
import { tracking } from '../util/tracking';

type PlanSelectProps = {
  onSelect: (planId: string) => void;
  plans?: Plan[];
  currentPlanTier?: number | null;
};

// const freePlanId = '82a8b5f1-1959-4fc3-a37a-f6b8f16d2423';

const featureLabels: Record<string, string> = {
  displayBadge: 'Embed badge on your website',
  publishReports: 'Publish and share reports',
  selfAssessment: 'Self assessments',
  aiCompliancePlan: 'AI assisted compliance plan',
  questionnaireBuilder: 'Questionnaire Builder',
  managedSuppliers: 'Manage up to {value} suppliers',
  inviteSuppliers: 'Invite suppliers',
  importSuppliers: 'Import suppliers',
  manageSuppliers: 'Manage Suppliers',
  sendAssessments: 'Send Assessments',
};

const templateLabels: Record<string, string> = {
  supplier_esg_report: 'ESG',
  supplier_lksg_report: 'LkSG (Germany)',
  supplier_csddd_report: 'CSDDD',
  supplier_fdv_report: 'FDV (France)',
  supplier_csrd_report: 'CSRD',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatFeatures(features: any): React.ReactNode[] {
  const result: React.ReactNode[] = [];

  Object.entries(features).forEach(([key, value]) => {
    if (typeof value === 'boolean' && value) {
      if (featureLabels[key]) result.push(featureLabels[key]);
    } else if (typeof value === 'number') {
      if (featureLabels[key]) {
        result.push(featureLabels[key].replace('{value}', String(value)));
      }
    } else if (key === 'selfAssessmentTemplateKeys' && Array.isArray(value)) {
      const templates = value
        .map((templateKey: string) => templateLabels[templateKey])
        .filter(Boolean);
      if (templates.length > 0) {
        result.push(
          <>
            Self assessment questionnaires:
            <br />
            <strong>{templates.join(', ')}</strong>
          </>
        );
      }
    }
  });

  return result;
}

export default function PlanSelect({
  onSelect,
  plans = [],
  currentPlanTier,
}: PlanSelectProps) {
  const [planType, setPlanType] = useState<string>('basic');

  // Show the other plan as most popular if user is already on the most popular one
  const most_popular_plan_tier = currentPlanTier === 2 ? 0 : 2;

  return (
    <Container>
      {!planType ? (
        <Row className="gy-4 justify-content-center">
          <Col xs={12} md={3}>
            <Card className={`h-100 shadow-sm border-1 rounded-3`}>
              <Card.Img
                variant="top"
                src={SupplierPlanImage}
                alt="Supplier"
                style={{ height: '200px', objectFit: 'cover' }}
              />
              <Card.Body className="d-flex flex-column">
                <p className="text-center text-muted">
                  I want to manage my own compliance, create reports and share
                  them with my buyers and partners.
                </p>
                <Button
                  variant="primary"
                  size="lg"
                  className="w-100 mt-auto"
                  onClick={() => {
                    tracking.log('UI', 'plan_type_supplier', {
                      planType: 'basic',
                    });
                    setPlanType('basic');
                  }}
                >
                  Supplier Plans
                </Button>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} md={3}>
            <Card className={`h-100 shadow-sm border-1 rounded-3`}>
              <Card.Img
                variant="top"
                src={ManagerPlanImage}
                alt="Compliance Manager"
                style={{ height: '200px', objectFit: 'cover' }}
              />
              <Card.Body className="d-flex flex-column">
                <p className="text-center text-muted">
                  I want to manage my suppliers' compliance and keep track of
                  their performance and analyze risks.
                </p>
                <Button
                  variant="primary"
                  size="lg"
                  className="w-100 mt-auto"
                  onClick={() => {
                    tracking.log('UI', 'plan_type_manager', {
                      planType: 'admin',
                    });
                    setPlanType('admin');
                  }}
                >
                  Compliance Manager Plans
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        <>
          <Row className="gy-4 justify-content-center mt-4">
            {plans
              .filter((p) => {
                if (currentPlanTier && currentPlanTier !== 1 && p.tier === 1) {
                  return false;
                }
                return p.type === planType;
              })
              .map((plan) => (
                <Col key={plan.name} className="plan-select-col">
                  <Card
                    className={`h-100 shadow-sm ${
                      plan.tier === most_popular_plan_tier
                        ? 'border-primary border-3 rounded-3'
                        : ''
                    }`}
                    style={
                      plan.tier === most_popular_plan_tier
                        ? { transform: 'scale(1.05)' }
                        : {}
                    }
                  >
                    <Card.Header
                      className={`text-center fw-bold${
                        plan.tier === most_popular_plan_tier
                          ? ' bg-primary text-white'
                          : ' bg-light'
                      }`}
                    >
                      {plan.name}
                      {plan.tier === most_popular_plan_tier && (
                        <img
                          src={mostPopularBadge}
                          alt="Most Popular"
                          width="40"
                          className="most-popular-badge"
                        />
                      )}
                    </Card.Header>
                    <Card.Body className="d-flex flex-column">
                      {plan.tier === 5 ? (
                        <p className="text-center text-muted">
                          Need a custom plan?
                        </p>
                      ) : (
                        <p>
                          <span className="display-6 fw-bold">
                            {plan.price}
                          </span>
                          {plan.currency}/m
                        </p>
                      )}
                      <ul className="list-unstyled mb-4 flex-grow-1">
                        {plan.features &&
                          formatFeatures(plan.features).map((label, i) => (
                            <li key={i} className="text-start">
                              <i
                                className="bi bi-check-circle-fill me-2"
                                style={{ color: '#2a245a' }}
                              ></i>
                              {label}
                            </li>
                          ))}
                      </ul>
                      {plan.tier === 5 ? (
                        <Button
                          variant="primary"
                          size="lg"
                          className="w-100 mt-auto"
                          onClick={() => {
                            tracking.log('UI', 'plan_contact', {
                              planType: planType,
                              planId: plan.id,
                              planTier: plan.tier,
                            });
                            window.location.href = 'mailto:info@supplypassport.co';
                          }}
                        >
                          Contact Us
                        </Button>
                      ) : (
                        <Button
                          variant={
                            plan.tier === most_popular_plan_tier
                              ? 'primary'
                              : 'outline-primary'
                          }
                          size="lg"
                          disabled={
                            plan.tier === 3 || currentPlanTier === plan.tier
                          }
                          className="w-100 mt-auto"
                          onClick={() => {
                            tracking.log('UI', 'plan_select', {
                              planType: planType,
                              planId: plan.id,
                              planTier: plan.tier,
                            });
                            onSelect(plan.id);
                          }}
                        >
                          {plan.tier === 3
                            ? 'Coming Soon'
                            : currentPlanTier === plan.tier
                            ? 'Current Plan'
                            : 'Select Plan'}
                        </Button>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
          </Row>
        </>
      )}
    </Container>
  );
}
